import React from 'react'
import { GetServerSideProps } from 'next'
import { SSRData } from 'next-urql'
import termsOfServiceContent from 'docs/terms-of-service.md'
import { Provider as UrqlProvider, createClient, ssrExchange } from 'urql'
import { isServerSide } from 'lib/env'
import { markdownToHtml } from 'lib/markdown'
import { getUrqlClientConfig } from 'hocs/withGql'
import { getVerticalData } from 'hocs/withVertical'
import { MeContext, fetchMe } from 'hooks/useMe'
import { VerticalContext } from 'hooks/useVerticalData'
import { HtmlContent } from 'components/HtmlContent'
import AppShell from 'components/appShell'

function TermsOfServices({ html }: { html: string }) {
    return (
        <AppShell title="Terms of service" footer={true} contentMaxWidth={'90ch'}>
            <HtmlContent html={html} p={{ base: '16px', md: '24px' }} />
        </AppShell>
    )
}

const cacheOptions = {}

type TermsOfServiceProps = {
    verticalData: React.ContextType<typeof VerticalContext>
    me: React.ContextType<typeof MeContext>
    ssrData: SSRData
    html: string
}

const TermsOfServicePageWrapped = ({
    verticalData,
    me,
    ssrData,
    html,
}: TermsOfServiceProps) => {
    const client = React.useMemo(() => {
        const ssr = ssrExchange({ isClient: !isServerSide, initialState: ssrData })
        return createClient(getUrqlClientConfig(ssr, undefined, cacheOptions))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <UrqlProvider value={client}>
            <MeContext.Provider value={me}>
                <VerticalContext.Provider value={verticalData}>
                    <TermsOfServices html={html} />
                </VerticalContext.Provider>
            </MeContext.Provider>
        </UrqlProvider>
    )
}

export default TermsOfServicePageWrapped

export const getServerSideProps: GetServerSideProps<TermsOfServiceProps> = async function (
    ctx,
) {
    const html = await markdownToHtml(termsOfServiceContent)

    const ssr = ssrExchange({ isClient: !isServerSide })
    const urqlClient = createClient(getUrqlClientConfig(ssr, ctx, cacheOptions))

    const me = await fetchMe(urqlClient, ctx)

    return {
        props: {
            me,
            html,
            verticalData: getVerticalData(ctx),
            ssrData: ssr.extractData(),
        },
    }
}
